import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { mediaUrl } from "@/lib/api";
import { coverOf, formatKm, formatPrice, STATUS_COLORS } from "@/lib/format";
import { cn } from "@/lib/utils";

export const StatusBadge = ({ status, light }) => {
  const { t } = useLang();
  return (
    <span data-testid="vehicle-status" className={cn("mono inline-flex items-center gap-2 text-[0.6rem]", light ? "text-[#0e1013]" : "text-[#e8eaee]")}>
      <span className="status-dot" style={{ background: STATUS_COLORS[status] || "#8b95a5" }} />
      {t(`st_${status}`)}
    </span>
  );
};

export const VehicleCard = ({ v, large, className }) => {
  const { t, lang, bi } = useLang();
  const cover = coverOf(v);
  const price = v.price_visible && v.price != null ? formatPrice(v.price, v.currency, lang) : t("price_on_request");
  return (
    <Link to={`/vehicles/${v.slug}`} data-testid={`vehicle-card-${v.slug}`} className={cn("group block", className)}>
      <div className={cn("hover-zoom relative overflow-hidden bg-[#0e1013]", large ? "aspect-[4/3] lg:aspect-[16/10]" : "aspect-[4/3]")}>
        {cover.thumb && (
          <img src={mediaUrl(large ? cover.full : cover.thumb)} alt={cover.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-90" />
        <div className="absolute left-4 top-4 rounded-sm bg-black/45 px-2.5 py-1.5 backdrop-blur-md">
          <StatusBadge status={v.status} />
        </div>
        <div className="absolute inset-x-0 bottom-0 flex translate-y-2 items-end justify-between p-5 opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <div className="mono flex flex-wrap gap-x-4 gap-y-1 text-[0.6rem] text-[#e8eaee]">
            {v.engine && <span>{v.engine}</span>}
            {v.power && <span>{v.power}</span>}
            {v.transmission && <span>{v.transmission}</span>}
          </div>
          <span className="mono hidden items-center gap-1 text-[0.6rem] text-white sm:inline-flex">
            {t("veh_view")} <ArrowUpRight size={14} />
          </span>
        </div>
      </div>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="mono text-[0.6rem] text-[#8b95a5]">{v.make}{v.year ? ` · ${v.year}` : ""}</p>
          <h3 className={cn("display-tight mt-1 truncate text-[#f4f5f7]", large ? "text-2xl md:text-3xl" : "text-xl")}>{v.model}</h3>
          {v.version && <p className="mt-0.5 truncate text-sm text-[#8b95a5]">{v.version}</p>}
          {large && bi(v.short_description) && <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#c7ccd4]">{bi(v.short_description)}</p>}
        </div>
        <div className="shrink-0 text-right">
          <p className="mono text-[0.68rem] text-[#f4f5f7]">{price}</p>
          <p className="mt-1 text-xs text-[#8b95a5]">{formatKm(v.mileage, lang)}{v.location ? ` · ${v.location}` : ""}</p>
        </div>
      </div>
    </Link>
  );
};
